import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Play, 
  ChevronRight, 
  Newspaper, 
  MapPin, 
  Utensils, 
  Mountain, 
  Video, 
  Camera, 
  Compass, 
  Heart,
  Clock,
  Sparkles,
  Loader2,
  Check,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import { NewsCard, NewsCardSkeleton } from "@/components/shared/NewsCard";
import { VideoCard, VideoCardSkeleton } from "@/components/shared/VideoCard";
import { DestinationCard, DestinationCardSkeleton } from "@/components/shared/DestinationCard";
import { AdPlaceholder, AdBanner } from "@/components/shared/AdPlaceholder";
import { SocialNetworksSection } from "@/components/shared/SocialNetworks";
import YouTubeSection from "@/components/shared/YouTubeSection";
import WeatherWidget from "@/components/WeatherWidget";
import axios from "axios";
import { API } from "@/App";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_newshub-choluteca/artifacts/0b71qo3w_DJI_0255.JPG";

const exploreChips = [
  { label: "Noticias", icon: Newspaper, href: "/noticias", color: "bg-blue-500" },
  { label: "Memorias", icon: Camera, href: "/memorias", color: "bg-purple-500" },
  { label: "Guías", icon: Compass, href: "/guias", color: "bg-amber-500" },
  { label: "Destinos", icon: MapPin, href: "/destinos", color: "bg-emerald-500" },
  { label: "Experiencias", icon: Mountain, href: "/experiencias", color: "bg-rose-500" },
  { label: "Sabores", icon: Utensils, href: "/sabores", color: "bg-orange-500" },
  { label: "Videos", icon: Video, href: "/videos", color: "bg-red-500" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function HomePage() {
  const [featuredContent, setFeaturedContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/public/featured`);
        setFeaturedContent(response.data);
      } catch (error) {
        console.error("Error fetching featured content:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
    // Track page view
    axios.post(`${API}/track-page-view?page=home`).catch(() => {});
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      toast.error("Por favor ingresa un email válido");
      return;
    }
    
    setNewsletterLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success(response.data.message);
      setNewsletterSuccess(true);
      setNewsletterEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al suscribirse");
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section - Full Impact */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Honduras paisaje"
            className="w-full h-full object-cover scale-105 animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        </div>

        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-40 left-20 w-48 h-48 bg-secondary/20 rounded-full blur-2xl animate-pulse delay-1000" />
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="flex items-center gap-3 mb-6"
              >
                <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm">
                  <Sparkles className="h-4 w-4 text-accent" />
                  El Pulso del Sur
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6"
              >
                Descubre la
                <br />
                <span className="relative">
                  <span className="text-accent">Belleza</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-3 text-accent/50" viewBox="0 0 200 12">
                    <path d="M0,8 Q50,0 100,8 T200,8" stroke="currentColor" strokeWidth="3" fill="none" />
                  </svg>
                </span>
                {" "}de Choluteca
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-lg sm:text-xl text-white/85 mb-8 leading-relaxed"
              >
                Tu portal a las noticias, destinos increíbles, cultura vibrante y sabores auténticos del sur de Honduras.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/noticias">
                  <Button
                    size="lg"
                    className="bg-accent text-brand-text hover:bg-accent/90 rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-accent/25 hover:shadow-accent/40 transition-all hover:scale-105"
                    data-testid="hero-explore-btn"
                  >
                    Explorar Ahora
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/videos">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/30 text-white hover:bg-white hover:text-primary rounded-full px-8 py-6 text-base font-medium backdrop-blur-sm transition-all hover:scale-105"
                    data-testid="hero-video-btn"
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Ver Videos
                  </Button>
                </Link>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex gap-8 mt-12"
              >
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">50+</p>
                  <p className="text-sm text-white/60">Destinos</p>
                </div>
                <div className="w-px bg-white/20" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-accent">100+</p>
                  <p className="text-sm text-white/60">Historias</p>
                </div>
                <div className="w-px bg-white/20" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">24/7</p>
                  <p className="text-sm text-white/60">Noticias</p>
                </div>
              </motion.div>
            </div>

            {/* Hero Right - Feature Cards */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Main Featured Card */}
                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 border border-white/20">
                  <Badge className="bg-accent text-brand-text mb-3">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Trending
                  </Badge>
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    Lo Más Leído Hoy
                  </h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-2">
                    Descubre las noticias más importantes y los destinos más visitados de la semana.
                  </p>
                  <Link to="/noticias" className="text-accent text-sm font-medium flex items-center hover:underline">
                    Ver más <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>

                {/* Floating Card */}
                <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl p-4 shadow-xl max-w-[200px] animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Camera className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-brand-text text-sm">Memorias</p>
                      <p className="text-xs text-brand-textMuted">Comparte tu historia</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2 text-white/60">
            <span className="text-xs uppercase tracking-wider">Explorar</span>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-1">
              <motion.div 
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-1.5 h-1.5 bg-accent rounded-full"
              />
            </div>
          </div>
        </motion.div>
      </section>

      {/* Top Ad Banner */}
      <div className="py-6 bg-white border-b border-gray-100">
        <AdBanner />
      </div>

      {/* Explore Categories - Interactive Cards */}
      <section className="py-16 bg-gradient-to-b from-white to-gray-50" data-testid="explore-chips-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="text-center mb-10"
          >
            <motion.h2 variants={itemVariants} className="font-heading text-3xl md:text-4xl font-bold text-brand-text mb-3">
              Explora Por Categoría
            </motion.h2>
            <motion.p variants={itemVariants} className="text-brand-textSecondary max-w-2xl mx-auto">
              Descubre todo lo que Choluteca tiene para ofrecer
            </motion.p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4"
          >
            {exploreChips.map((chip, index) => (
              <motion.div key={chip.label} variants={itemVariants}>
                <Link
                  to={chip.href}
                  className="group flex flex-col items-center p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                  data-testid={`chip-${chip.label.toLowerCase()}`}
                >
                  <div className={`w-14 h-14 rounded-2xl ${chip.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
                    <chip.icon className="h-7 w-7 text-white" />
                  </div>
                  <span className="font-medium text-brand-text text-sm">{chip.label}</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Latest News with Sidebar Ad */}
      <section className="py-16 md:py-24 bg-white" data-testid="news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-primary rounded-full" />
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-text">
                  Últimas Noticias
                </h2>
              </div>
              <p className="text-brand-textSecondary">
                Lo más reciente de Choluteca y Honduras
              </p>
            </div>
            <Link
              to="/noticias"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-primary/5 hover:bg-primary/10 rounded-full text-primary font-medium transition-colors"
              data-testid="see-all-news"
            >
              Ver todas
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main News Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="bento-grid">
                  <div className="bento-featured">
                    <NewsCardSkeleton variant="featured" />
                  </div>
                  {[1, 2, 3, 4].map((i) => (
                    <NewsCardSkeleton key={i} />
                  ))}
                </div>
              ) : featuredContent?.news?.length > 0 ? (
                <div className="bento-grid">
                  {featuredContent.news[0] && (
                    <div className="bento-featured">
                      <NewsCard post={featuredContent.news[0]} variant="featured" />
                    </div>
                  )}
                  {featuredContent.news.slice(1, 5).map((post, idx) => (
                    <div key={post.post_id} className={idx === 1 ? "bento-tall" : ""}>
                      <NewsCard post={post} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gray-50 rounded-3xl">
                  <Newspaper className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No hay noticias publicadas aún</p>
                </div>
              )}
            </div>

            {/* Sidebar with Weather and Ad */}
            <div className="hidden lg:block space-y-6">
              <WeatherWidget />
              <AdPlaceholder size="rectangle" className="w-full sticky top-24" testId="ad-news-sidebar" />
            </div>
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/noticias">
              <Button variant="outline" className="rounded-full">
                Ver todas las noticias
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mid-Page Ad Banner */}
      <div className="py-8 bg-gray-50">
        <AdBanner />
      </div>

      {/* Videos Section - Cinematic Dark */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#0B0F14] via-[#121820] to-[#0B0F14] relative overflow-hidden" data-testid="videos-section">
        {/* Background Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Play className="h-6 w-6 text-accent" />
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                  Honduras en Video
                </h2>
              </div>
              <p className="text-gray-400">
                Explora nuestra región a través de experiencias visuales
              </p>
            </div>
            <Link
              to="/videos"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white font-medium transition-colors"
              data-testid="see-all-videos"
            >
              Ver todos
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <VideoCardSkeleton key={i} />
              ))}
            </div>
          ) : featuredContent?.videos?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredContent.videos.map((video) => (
                <VideoCard key={video.post_id} post={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Video className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No hay videos publicados aún</p>
            </div>
          )}
        </div>
      </section>

      {/* Memorias Section - Vibrant & Engaging */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/5 via-white to-accent/5 relative" data-testid="memorias-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Heart className="h-6 w-6 text-rose-500" />
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-text">
                  Memorias de Nuestra Tierra
                </h2>
              </div>
              <p className="text-brand-textSecondary">
                Historias, tradiciones y recuerdos que nos unen
              </p>
            </div>
            <Link
              to="/memorias"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-primary/5 hover:bg-primary/10 rounded-full text-primary font-medium transition-colors"
              data-testid="see-all-memorias"
            >
              Ver todas
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* CTA Card - Interactive */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1 bg-gradient-to-br from-primary via-primary to-secondary rounded-3xl p-8 flex flex-col justify-between relative overflow-hidden group"
            >
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/20 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Camera className="h-8 w-8 text-accent" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-4">
                  ¿Tienes fotos antiguas o historias?
                </h3>
                <p className="text-white/80 mb-6 leading-relaxed">
                  Comparte tus recuerdos de familia, tradiciones perdidas y ayuda a preservar nuestra historia colectiva.
                </p>
              </div>
              <Link to="/compartir">
                <Button className="w-full bg-accent text-brand-text hover:bg-accent/90 rounded-full font-semibold shadow-lg group-hover:shadow-accent/25 transition-all" data-testid="share-memory-home-btn">
                  <Camera className="mr-2 h-5 w-5" />
                  Compartir mi memoria
                </Button>
              </Link>
            </motion.div>

            {/* Featured Memories Grid */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <Badge className="bg-purple-100 text-purple-700 mb-4">Antes / Después</Badge>
                  <h4 className="font-heading text-lg font-bold text-brand-text mb-2">
                    El centro de Choluteca
                  </h4>
                  <p className="text-sm text-brand-textSecondary mb-4">
                    Comparte fotos de cómo era tu ciudad antes y cómo es ahora.
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-1 text-sm text-rose-500">
                      <Heart className="h-4 w-4 fill-current" /> 12 votos
                    </span>
                    <Link to="/memorias" className="text-primary text-sm font-medium hover:underline">
                      Ver más
                    </Link>
                  </div>
                </motion.div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <Badge className="bg-orange-100 text-orange-700 mb-4">Tradición</Badge>
                  <h4 className="font-heading text-lg font-bold text-brand-text mb-2">
                    Fiestas patronales
                  </h4>
                  <p className="text-sm text-brand-textSecondary mb-4">
                    Recuerdos de las celebraciones que nos unen como comunidad.
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-1 text-sm text-rose-500">
                      <Heart className="h-4 w-4 fill-current" /> 8 votos
                    </span>
                    <Link to="/memorias" className="text-primary text-sm font-medium hover:underline">
                      Ver más
                    </Link>
                  </div>
                </motion.div>

                {/* Ad within memories section */}
                <div className="md:col-span-2">
                  <AdPlaceholder size="banner" className="w-full" testId="ad-memorias-banner" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Section */}
      <YouTubeSection />

      {/* Destinations Section - Magazine Style */}
      {/* Destinos Destacados - Temporalmente oculto
      <section className="py-16 md:py-24 bg-white" data-testid="destinations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-6 w-6 text-emerald-500" />
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-text">
                  Destinos Destacados
                </h2>
              </div>
              <p className="text-brand-textSecondary">
                Lugares increíbles que debes visitar
              </p>
            </div>
            <Link
              to="/destinos"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-primary/5 hover:bg-primary/10 rounded-full text-primary font-medium transition-colors"
              data-testid="see-all-destinations"
            >
              Ver todos
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <DestinationCardSkeleton key={i} />
              ))}
            </div>
          ) : featuredContent?.destinations?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredContent.destinations.map((dest) => (
                <DestinationCard key={dest.post_id} post={dest} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-3xl">
              <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay destinos publicados aún</p>
            </div>
          )}
        </div>
      </section>
      */}

      {/* Guides Quick Access - Temporalmente oculto
      <section className="py-16 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="guides-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Compass className="h-6 w-6 text-amber-600" />
                <span className="text-amber-600 font-medium">Guías Prácticas</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-text mb-4">
                ¿Qué hacer en Choluteca?
              </h2>
              <p className="text-brand-textSecondary text-lg mb-6">
                Descubre las mejores actividades, rutas y experiencias con nuestras guías detalladas creadas por locales.
              </p>
              <Link to="/guias">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="explore-guides-btn">
                  Explorar Guías
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { title: "Ruta del café", time: "4 horas", icon: "☕" },
                { title: "Playas del sur", time: "1 día", icon: "🏖️" },
                { title: "Historia colonial", time: "3 horas", icon: "🏛️" },
                { title: "Gastronomía local", time: "2 horas", icon: "🍽️" },
              ].map((guide, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-2xl mb-3 block">{guide.icon}</span>
                  <h4 className="font-heading font-bold text-brand-text">{guide.title}</h4>
                  <p className="text-sm text-brand-textMuted flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3" /> {guide.time}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Bottom Ad Banner */}
      <div className="py-8 bg-white">
        <AdBanner />
      </div>

      {/* Social Networks Section */}
      <SocialNetworksSection />

      {/* Newsletter CTA - Enhanced */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-primary via-primary to-secondary relative overflow-hidden" data-testid="newsletter-section">
        {/* Decorative */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm mb-6">
              <Sparkles className="h-4 w-4 text-accent" />
              No te pierdas nada
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              Mantente Informado
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Suscríbete a nuestro newsletter y recibe las últimas noticias, eventos y novedades directamente en tu correo.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Tu correo electrónico"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterLoading || newsletterSuccess}
                className="flex-1 px-6 py-4 rounded-full text-brand-text focus:outline-none focus:ring-4 focus:ring-accent/50 shadow-lg disabled:opacity-50"
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                size="lg"
                disabled={newsletterLoading || newsletterSuccess}
                className="bg-accent text-brand-text hover:bg-accent/90 rounded-full px-8 font-semibold shadow-lg hover:shadow-accent/25 transition-all disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {newsletterLoading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Suscribiendo...</>
                ) : newsletterSuccess ? (
                  <><Check className="mr-2 h-5 w-5" />¡Suscrito!</>
                ) : (
                  <>Suscribirse<ArrowRight className="ml-2 h-5 w-5" /></>
                )}
              </Button>
            </form>
            <p className="text-white/50 text-sm mt-4">
              Sin spam. Cancela cuando quieras.
            </p>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
